import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Wallet, ArrowRight, Lock, Clock, IndianRupee, LayoutDashboard, CheckCircle2 } from "lucide-react";

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.role === "admin") nav("/admin");
    else if (user && user.role === "user") nav("/dashboard");
  }, [user, nav]);

  const features = [
    { icon: Lock, title: "Manual & Secure", desc: "Every deposit and payout is verified by an admin. No auto-gateways." },
    { icon: IndianRupee, title: "Live INR Ledger", desc: "Accurate balances with reserved-fund locking on pending withdrawals." },
    { icon: Clock, title: "IST Timestamps", desc: "Exact create, approve, reject & paid times in Indian Standard Time." },
    { icon: LayoutDashboard, title: "Dual Panels", desc: "Role-based Admin control room and a clean personal User wallet." },
  ];

  return (
    <div className="min-h-screen vp-grid-bg text-slate-100">
      <header className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
        <Brand size="md" />
        <div className="flex items-center gap-2">
          <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5" onClick={() => nav("/login")} data-testid="nav-user-login">
            User Login
          </Button>
          <Button className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold" onClick={() => nav("/admin/login")} data-testid="nav-admin-login">
            Admin Login
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <section className="pt-14 sm:pt-24 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-mono uppercase tracking-widest text-emerald-300">
              <ShieldCheck className="h-3.5 w-3.5" /> Secure Treasury
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              The precise way to manage <span className="text-emerald-400">deposits</span> & <span className="text-cyan-400">payouts</span>.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              VantorPay.com is a secure, admin-controlled treasury. Assign UPI IDs, verify UTRs, approve requests, and keep a bullet-proof INR ledger — all by hand, no gateways.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold h-12 px-7 text-base" onClick={() => nav("/login")} data-testid="hero-user-btn">
                <Wallet className="mr-2 h-5 w-5" /> Enter User Panel <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-7 text-base border-slate-700 bg-white/5 hover:bg-white/10 text-slate-100" onClick={() => nav("/admin/login")} data-testid="hero-admin-btn">
                <ShieldCheck className="mr-2 h-5 w-5" /> Admin Control Room
              </Button>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              {["No payment gateway", "Reserved-fund safety", "Full audit trail"].map((t) => (
                <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t}</span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
            <div className="vp-card vp-glow rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-widest text-slate-400">Available Balance</span>
                <Brand size="sm" />
              </div>
              <div className="mt-3 font-display text-4xl font-extrabold text-emerald-400">₹4,82,500.00</div>
              <div className="mt-1 text-sm text-slate-500 font-mono">Reserved ₹25,000.00 · IST synced</div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[["Total Deposits", "₹12,40,000"], ["Total Payouts", "₹7,57,500"], ["Pending Deposit", "₹30,000"], ["Pending Payout", "₹25,000"]].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-white/5 bg-black/20 p-3">
                    <div className="text-xs text-slate-500">{l}</div>
                    <div className="mt-1 font-mono font-semibold text-slate-100">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
                <span className="text-sm text-slate-300 font-mono">UTR 2029• verified</span>
                <span className="rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-xs text-emerald-300 font-mono">APPROVED</span>
              </div>
            </div>
          </motion.div>
        </section>

        <section className="pb-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="vp-card rounded-xl p-5 hover:border-emerald-500/30 transition-colors">
              <div className="h-10 w-10 grid place-items-center rounded-lg bg-emerald-500/10 text-emerald-400"><f.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-semibold text-lg">{f.title}</h3>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </section>
      </main>

      <footer className="border-t border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <Brand size="sm" />
          <p>© {new Date().getFullYear()} VantorPay.com · Secure manual payment management</p>
          <Link to="/admin/login" className="hover:text-emerald-400 transition-colors">Admin Access</Link>
        </div>
      </footer>
    </div>
  );
}
